import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../lib/api.js";
import { common } from "../copy/common.js";
import { errors } from "../copy/errors.js";
import { lists } from "../copy/lists.js";
import { Banner } from "../components/ui.js";
import { ListEditor, type ListEditorValues } from "../components/ListEditor.js";

interface ListDetail {
  id: string;
  title: string;
  source_hint: string | null;
  lang_b: "en" | "fr";
  due_date: string | null;
  visibility: "private" | "class" | string;
  is_owner: boolean;
  items: Array<{ a: string; b: string }>;
}

export function ListEditScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState<ListDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!id) return;
    void api<ListDetail>(`/api/lists/${id}`)
      .then(setList)
      .catch((err: unknown) =>
        setLoadError(err instanceof ApiError ? err.message : errors.notFound),
      );
  }, [id]);

  const save = async (values: ListEditorValues) => {
    if (!list) return;
    setError(null);
    setBusy(true);
    try {
      await api(`/api/lists/${list.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          title: values.title,
          due_date: values.dueDate,
          source_hint: values.sourceHint,
          items: values.items,
        }),
      });
      navigate(`/lists/${list.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!list) return;
    setError(null);
    setBusy(true);
    try {
      await api(`/api/lists/${list.id}`, { method: "DELETE" });
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : errors.failed);
      setBusy(false);
    }
  };

  if (loadError) return <Banner>{loadError}</Banner>;
  if (!list) return <p className="text-ink-soft">{common.loading}</p>;
  // Someone else's list is read-only; the detail view offers "Kopieren" instead.
  if (!list.is_owner) return <Navigate to={`/lists/${list.id}`} replace />;

  if (confirmDelete) {
    return (
      <div className="mx-auto max-w-md space-y-5">
        <h1 className="text-3xl sm:text-4xl">{lists.delete.title}</h1>
        <p className="text-ink-soft">
          {list.visibility === "class"
            ? lists.delete.bodyShared(list.title)
            : lists.delete.body(list.title)}
        </p>
        {error ? <Banner>{error}</Banner> : null}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => void remove()}
            disabled={busy}
            className="min-h-12 rounded-2xl bg-rose px-5 py-3 font-semibold text-white hover:brightness-95 disabled:opacity-50"
          >
            {busy ? common.loading : lists.delete.confirm}
          </button>
          <button
            type="button"
            onClick={() => setConfirmDelete(false)}
            className="min-h-12 rounded-2xl border-2 border-line px-5 py-3 font-semibold"
          >
            {lists.delete.keep}
          </button>
        </div>
      </div>
    );
  }

  return (
    <ListEditor
      mode="edit"
      backTo={`/lists/${list.id}`}
      busy={busy}
      error={error}
      initial={{
        title: list.title,
        langB: list.lang_b,
        dueDate: list.due_date,
        sourceHint: list.source_hint,
        items: list.items.map((item) => ({ a: item.a, b: item.b })),
      }}
      onSubmit={(values) => void save(values)}
      onDelete={() => setConfirmDelete(true)}
    />
  );
}
